import { ref, onUnmounted, toRaw } from "vue"
import type { VideoMeta, WorkerOutbound, ResolutionChoice, TrimRange, MediaMode } from "@/lib/types"
import { payloadSize, type UploadPayload } from "@/lib/commons-api"

export interface EncodeResult {
  /** ArrayBuffer from the encoder, or the source Blob on a passthrough. */
  bytes: UploadPayload; totalBytes: number; encodeMs: number
  audioIncluded: boolean; passthrough: boolean
  /** OPFS key of the output when it went to disk, so an interrupted
   *  upload can be resumed with these exact bytes. */
  outputKey?: string
}

export function useEncoder() {
  const phase = ref<"idle" | "encoding" | "passthrough" | "done" | "error" | "cancelled">("idle")
  const progress = ref(0)
  const errorMsg = ref<string | null>(null)
  const lastResult = ref<EncodeResult | null>(null)

  let worker: Worker | null = null
  let runId = 0

  function makeWorker(): Worker {
    return new Worker(new URL("@/workers/encoder.worker.ts", import.meta.url), { type: "module" })
  }

  function reset() {
    phase.value = "idle"; progress.value = 0
    errorMsg.value = null; lastResult.value = null
  }

  function cancel() {
    if (worker && phase.value === "encoding") {
      worker.postMessage({ kind: "cancel" })
      setTimeout(() => {
        if (worker) { try { worker.terminate() } catch { /* */ } ; worker = null }
        phase.value = "cancelled"
      }, 500)
    }
  }

  /**
   * Encode (or passthrough) a single file.
   *
   * @param trim Optional trim range, in seconds. When provided AND active
   *             (start>0 or end<duration), passthrough is skipped and the
   *             worker re-encodes through mediabunny's trim option.
   */
  async function encode(
    file: File, meta: VideoMeta, resolution: ResolutionChoice,
    trim?: TrimRange | null, mediaMode?: MediaMode
  ): Promise<EncodeResult> {
    reset(); runId++; const myRunId = runId
    if (worker) { try { worker.terminate() } catch { /* */ } ; worker = null }
    worker = makeWorker()
    const w = worker

    return new Promise<EncodeResult>((resolve, reject) => {
      let collectedBytes: UploadPayload | null = null

      w.onmessage = (e: MessageEvent<WorkerOutbound>) => {
        if (myRunId !== runId) return
        const msg = e.data
        switch (msg.kind) {
          case "phase": phase.value = msg.phase; break
          case "progress": progress.value = msg.ratio ?? msg.progress ?? 0; break
          case "log": break
          case "webm-chunk": collectedBytes = msg.bytes; break
          case "done": {
            if (!collectedBytes) { reject(new Error("Worker reported done but no bytes received")); return }
            phase.value = "done"; progress.value = 1
            const result: EncodeResult = {
              bytes: collectedBytes, totalBytes: msg.totalBytes ?? payloadSize(collectedBytes),
              encodeMs: msg.encodeMs, audioIncluded: msg.audioIncluded,
              passthrough: msg.passthrough, outputKey: msg.outputKey
            }
            lastResult.value = result; resolve(result)
            break
          }
          case "cancelled": phase.value = "cancelled"; reject(new Error("cancelled")); break
          case "error":
            phase.value = "error"; errorMsg.value = msg.message
            reject(new Error(msg.message)); break
        }
      }

      w.onerror = (err) => {
        if (myRunId !== runId) return
        phase.value = "error"
        errorMsg.value = err.message || "Worker crashed"
        reject(new Error(errorMsg.value))
      }

      w.postMessage({
        kind: "start",
        file: toRaw(file),
        meta: toRaw(meta),
        resolution,
        trim: trim ? { start: trim.start, end: trim.end } : null,
        mediaMode: mediaMode ?? "video-audio"
      })
    })
  }

  function terminate() {
    if (worker) { try { worker.terminate() } catch { /* */ } ; worker = null }
  }
  onUnmounted(terminate)

  return { phase, progress, errorMsg, lastResult, encode, reset, cancel, terminate }
}
